import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchActiveAgents, ActiveAgentsResponse } from '../../thunks/CallCenterThunks/fetchActiveAgents';

interface ActiveAgentsState {
  items: ActiveAgentsResponse | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ActiveAgentsState = {
  items: null,
  status: 'idle',
  error: null,
};

interface UpdateAgentInput {
  name: string; // full name like agentId@domain
  [key: string]: any;
}

interface UpdateAgentOutput extends UpdateAgentInput {
  agentId: string;
}

export const updateCallCenterAgentStatus = createAsyncThunk<UpdateAgentOutput, UpdateAgentInput>(
  'activeAgents/updateActiveAgent',
  async (data, { rejectWithValue }) => {
    try {
      const object = { ...data };
      const name = object.name.split('@')[0]; // get agent ID
      return { ...object, agentId: name };
    } catch (error) {
      console.error('Update error:', error);
      return rejectWithValue('Failed to update agent status');
    }
  }
);

export const removeAllQueueMembers = createAsyncThunk<boolean>(
  'activeAgents/removeAllQueueMembers',
  async (_, { rejectWithValue }) => {
    try {
      return true;
    } catch (error) {
      console.error('Remove error:', error);
      return rejectWithValue('Failed to remove queue members');
    }
  }
);

const activeAgentsSlice = createSlice({
  name: 'activeAgents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveAgents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchActiveAgents.fulfilled, (state, action: PayloadAction<ActiveAgentsResponse>) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchActiveAgents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Something went wrong';
      })
      .addCase(updateCallCenterAgentStatus.fulfilled, (state, action: PayloadAction<UpdateAgentOutput>) => {
        const updatedAgent = action.payload;

        if (!state.items?.agents) return;

        const agentIndex = state.items.agents.findIndex(
          (agent) => agent.name === updatedAgent.agentId
        );

        if (agentIndex !== -1) {
          state.items.agents[agentIndex] = {
            ...state.items.agents[agentIndex],
            ...updatedAgent,
            name: updatedAgent.agentId,
          };
        } else {
          // Add new agent if not found
          state.items.agents.push({
            ...updatedAgent,
            name: updatedAgent.agentId,
          } as any);
        }
      });
  },
});

export default activeAgentsSlice.reducer;
